import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { sendPasswordResetEmail } from 'firebase/auth';

interface User {
  uid: string;
  email: string;
  isAdmin: boolean;
  lastModified?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const userRolesRef = collection(db, 'userRoles');
      const snapshot = await getDocs(userRolesRef);
      
      const usersList = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
        isAdmin: doc.data().isAdmin || false,
        lastModified: doc.data().lastModified || new Date().toISOString()
      })) as User[];

      setUsers(usersList);
      setError('');
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (uid: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      // Don't allow deleting your own account
      if (uid === currentUser?.uid) {
        setError('You cannot delete your own account');
        return;
      }

      await deleteDoc(doc(db, 'userRoles', uid));
      setSuccess('User deleted successfully');
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const toggleAdminStatus = async (user: User) => {
    try {
      // Don't allow removing your own admin status
      if (user.uid === currentUser?.uid) {
        setError('You cannot modify your own admin status');
        return;
      }

      const userRef = doc(db, 'userRoles', user.uid);
      await setDoc(userRef, {
        ...user,
        isAdmin: !user.isAdmin,
        lastModified: new Date().toISOString(),
        modifiedBy: currentUser?.uid
      });

      setSuccess(`Admin status ${!user.isAdmin ? 'granted' : 'revoked'} successfully`);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      console.error('Error toggling admin status:', err);
      setError('Failed to update admin status');
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(`Password reset email sent to ${email}`);
    } catch (err: any) {
      console.error('Error sending password reset:', err);
      setError('Failed to send password reset email');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  if (loading) return <div>Loading users...</div>;

  return (
    <div className="user-management">
      <div className="header">
        <h2>Admin Users</h2>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <table className="users-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Status</th>
            <th>Last Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.uid}>
              <td>{user.email}</td>
              <td>
                <span className={`status ${user.isAdmin ? 'admin' : 'not-admin'}`}>
                  {user.isAdmin ? 'Admin' : 'Not Admin'}
                </span>
              </td>
              <td>{formatDate(user.lastModified || '')}</td>
              <td>
                <button
                  onClick={() => toggleAdminStatus(user)}
                  disabled={user.uid === currentUser?.uid}
                  className="action-button"
                >
                  {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                </button>
                <button
                  onClick={() => handleResetPassword(user.email)}
                  className="reset-button"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => handleDeleteUser(user.uid)}
                  disabled={user.uid === currentUser?.uid}
                  className="delete-button"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <style>{`
        .user-management {
          padding: 1rem;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .header h2 {
          margin: 0;
          color: #333;
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
        }

        .users-table th,
        .users-table td {
          padding: 0.75rem;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }

        .users-table th {
          background-color: #f8f9fa;
          font-weight: 600;
        }

        .status {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
        }

        .status.admin {
          background-color: #28a745;
          color: white;
        }

        .status.not-admin {
          background-color: #dc3545;
          color: white;
        }

        .action-button {
          margin-right: 0.5rem;
          padding: 0.25rem 0.5rem;
          border: none;
          border-radius: 4px;
          background-color: #007bff;
          color: white;
          cursor: pointer;
        }

        .reset-button {
          margin-right: 0.5rem;
          padding: 0.25rem 0.5rem;
          border: none;
          border-radius: 4px;
          background-color: #ffc107;
          color: #333;
          cursor: pointer;
        }

        .delete-button {
          padding: 0.25rem 0.5rem;
          border: none;
          border-radius: 4px;
          background-color: #dc3545;
          color: white;
          cursor: pointer;
        }

        .action-button:disabled,
        .reset-button:disabled,
        .delete-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .error-message {
          color: #dc3545;
          margin-bottom: 1rem;
        }

        .success-message {
          color: #28a745;
          margin-bottom: 1rem;
        }
      `}</style>
    </div>
  );
}

export default UserManagement; 